import { useMemo, useState } from 'react';
import { Download, BarChart2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Chart } from '@vnetwork/ui/charts';
import { useToast } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Button,
  Skeleton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@vnetwork/ui/primitives';
import {
  useLmsRecordingDurationUsage,
  useLmsTranscodingDurationUsage,
  useLmsJobCountsUsage,
  fetchLmsAnalysisCsv,
} from '@/api/media/live-analysis';
import type {
  LmsAnalysisDownloadType,
  LmsTranscodingDurationRange,
} from '@/api/media/live-analysis-types';
import {
  ANALYSIS_GROUP_ORDER,
  ANALYSIS_SERIES_GROUPS,
  buildRecordingData,
  buildTranscodingData,
  buildJobCountsData,
} from './analysis-utils';
import { ChartCard, hasSeriesData } from '../../_shared/AnalyticsKit';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import styles from './AnalysisPage.module.css';

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Per-group chart pair (recording + transcoding) ───────────────────────────

interface ChartPairProps {
  groupKey: string;
  year: number;
  yearOptions: number[];
  onYearChange: (y: number) => void;
  transcodingRange: LmsTranscodingDurationRange;
  onRangeChange: (r: LmsTranscodingDurationRange) => void;
  onDownload: (type: LmsAnalysisDownloadType) => void;
  downloadingType: LmsAnalysisDownloadType | null;
  recordingUsage: Parameters<typeof buildRecordingData>[0];
  transcodingUsage: Parameters<typeof buildTranscodingData>[0];
  currentYear: number;
  currentMonthIndex: number;
  recordingLoading: boolean;
  transcodingLoading: boolean;
  recordingError: boolean;
  transcodingError: boolean;
}

function GroupChartPair({
  groupKey,
  year,
  yearOptions,
  onYearChange,
  transcodingRange,
  onRangeChange,
  onDownload,
  downloadingType,
  recordingUsage,
  transcodingUsage,
  currentYear,
  currentMonthIndex,
  recordingLoading,
  transcodingLoading,
  recordingError,
  transcodingError,
}: ChartPairProps) {
  const group = ANALYSIS_SERIES_GROUPS[groupKey];
  const isOverview = groupKey === 'overview';

  const recordingData = useMemo(
    () => buildRecordingData(recordingUsage, year, currentYear, currentMonthIndex, group.recordingKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordingUsage, year, currentYear, currentMonthIndex, group.recordingKeys.join(',')],
  );

  const transcodingData = useMemo(
    () => buildTranscodingData(transcodingUsage, group.transcodingKeys),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transcodingUsage, group.transcodingKeys.join(',')],
  );

  return (
    <div className={styles.chartPair}>
      {/* Recording duration */}
      <ChartCard
        title={`${group.title} — Recording Duration`}
        description={group.subtitle}
        error={recordingError}
        actions={
          <div className={styles.chartActions}>
            <Select value={String(year)} onValueChange={(v) => onYearChange(Number(v))}>
              <SelectTrigger size="sm" style={{ width: 90 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isOverview && (
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Download size={13} />}
                disabled={downloadingType === 'recording-duration'}
                onClick={() => onDownload('recording-duration')}
              >
                {downloadingType === 'recording-duration' ? 'Downloading…' : 'CSV'}
              </Button>
            )}
          </div>
        }
      >
        {recordingLoading ? (
          <Skeleton variant="rect" height={240} />
        ) : !hasSeriesData(recordingData.rows, 'label') ? (
          <EmptyState
            icon={<BarChart2 size={20} />}
            title="No recording data"
            description={`No recording data for ${year}.`}
            size="sm"
          />
        ) : (
          <Chart
            type="line"
            data={recordingData.rows}
            xKey="label"
            series={recordingData.series}
            height={240}
            showLegend={recordingData.series.length > 1}
            valueFormatter={(v, key) => (key ? `${Number(v).toLocaleString()} Hour(s)` : Number(v).toLocaleString())}
          />
        )}
      </ChartCard>

      {/* Transcoding duration */}
      <ChartCard
        title={`${group.title} — Transcoding Duration`}
        description={group.subtitle}
        error={transcodingError}
        actions={
          <div className={styles.chartActions}>
            <Select
              value={transcodingRange}
              onValueChange={(v) => onRangeChange(v as LmsTranscodingDurationRange)}
            >
              <SelectTrigger size="sm" style={{ width: 120 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            {isOverview && (
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Download size={13} />}
                disabled={downloadingType === 'transcoding-duration'}
                onClick={() => onDownload('transcoding-duration')}
                title={`Export full-year transcoding report (${new Date().getFullYear()})`}
              >
                {downloadingType === 'transcoding-duration' ? 'Downloading…' : 'CSV'}
              </Button>
            )}
          </div>
        }
      >
        {transcodingLoading ? (
          <Skeleton variant="rect" height={240} />
        ) : !hasSeriesData(transcodingData.rows, 'label') ? (
          <EmptyState
            icon={<BarChart2 size={20} />}
            title="No transcoding data"
            description="No transcoding data for the selected period."
            size="sm"
          />
        ) : (
          <Chart
            type="line"
            data={transcodingData.rows}
            xKey="label"
            series={transcodingData.series}
            height={240}
            showLegend={transcodingData.series.length > 1}
            valueFormatter={(v, key) => (key ? `${Number(v).toLocaleString()} Hour(s)` : Number(v).toLocaleString())}
          />
        )}
      </ChartCard>
    </div>
  );
}

// ─── Main analysis page ───────────────────────────────────────────────────────

interface Props {
  serviceId: string;
}

/**
 * Live Transcoding usage analytics — recording duration, transcoding duration,
 * and job counts across all encoding groups (Overview / Transmux / H.264 / H.265).
 * CSV export available on Overview-group charts and Job Counts.
 */
export function LiveAnalysis({ serviceId }: Props) {
  const toast = useToast();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const yearOptions = [currentYear, currentYear - 1];

  const [year, setYear] = useState(currentYear);
  const [transcodingRange, setTranscodingRange] = useState<LmsTranscodingDurationRange>('7');
  const [downloadingType, setDownloadingType] = useState<LmsAnalysisDownloadType | null>(null);

  const { data: recordingUsage = [], isPending: recordingLoading, isError: recordingError } =
    useLmsRecordingDurationUsage(serviceId, year);

  const { data: transcodingUsage = [], isPending: transcodingLoading, isError: transcodingError } =
    useLmsTranscodingDurationUsage(serviceId, transcodingRange);

  const { data: jobCounts = [], isPending: jobCountsLoading, isError: jobCountsError } =
    useLmsJobCountsUsage(serviceId, year);

  const jobCountsData = useMemo(
    () => buildJobCountsData(jobCounts, year, currentYear, currentMonthIndex),
    [jobCounts, year, currentYear, currentMonthIndex],
  );

  const handleDownload = async (type: LmsAnalysisDownloadType) => {
    setDownloadingType(type);
    try {
      // Transcoding CSV endpoint only supports year-based export; pass current year.
      const csvYear = type === 'transcoding-duration' ? currentYear : year;
      const blob = await fetchLmsAnalysisCsv(serviceId, type, csvYear);
      triggerDownload(blob, `lms-${type}-${csvYear}.csv`);
    } catch (err) {
      toast.error((err as Error).message || `Failed to download ${type} CSV`);
    } finally {
      setDownloadingType(null);
    }
  };

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Analysis' }]} />}
        title="Usage Analytics"
        description="Recording, transcoding, and job-count analysis for live stream workloads."
      />
      <div className={styles.chartGrid}>
        {/* Per-group chart pairs */}
        {ANALYSIS_GROUP_ORDER.map((groupKey) => (
          <GroupChartPair
            key={groupKey}
            groupKey={groupKey}
            year={year}
            yearOptions={yearOptions}
            onYearChange={setYear}
            transcodingRange={transcodingRange}
            onRangeChange={setTranscodingRange}
            onDownload={(t) => void handleDownload(t)}
            downloadingType={downloadingType}
            recordingUsage={recordingUsage}
            transcodingUsage={transcodingUsage}
            currentYear={currentYear}
            currentMonthIndex={currentMonthIndex}
            recordingLoading={recordingLoading}
            transcodingLoading={transcodingLoading}
            recordingError={recordingError}
            transcodingError={transcodingError}
          />
        ))}

        {/* Job counts chart — full width */}
        <ChartCard
          title="Job Counts"
          description="Monthly max concurrent H.264/H.265 capacity and linear/event job counts."
          error={jobCountsError}
          actions={
            <div className={styles.chartActions}>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger size="sm" style={{ width: 90 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Download size={13} />}
                disabled={downloadingType === 'job-counts'}
                onClick={() => void handleDownload('job-counts')}
              >
                {downloadingType === 'job-counts' ? 'Downloading…' : 'CSV'}
              </Button>
            </div>
          }
        >
          {jobCountsLoading ? (
            <Skeleton variant="rect" height={240} />
          ) : !hasSeriesData(jobCountsData.rows, 'label') ? (
            <EmptyState
              icon={<BarChart2 size={20} />}
              title="No job count data"
              description={`No data for ${year}.`}
              size="sm"
            />
          ) : (
            <Chart
              type="bar"
              data={jobCountsData.rows}
              xKey="label"
              series={jobCountsData.series}
              height={240}
              showLegend
            />
          )}
        </ChartCard>
      </div>
    </Container>
  );
}

export default LiveAnalysis;
